import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const EFFECTIVE_DATE = 'April 6, 2026';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6">
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-sky-500 hover:text-sky-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="card p-8 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

        {/* Introduction */}
        <div className="card p-8 sm:p-10">
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of FLARE Social
            (flare-social.com), a SaaS social media scheduling and publishing platform
            operated by AKA IND Technologies ("AKA IND", "we", "us", or "our"). By
            creating an account or using the service, you agree to be bound by these Terms.
            If you do not agree to these Terms, do not use the service.
          </p>
        </div>

        {/* 1. Acceptance of Terms */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            1. Acceptance of Terms
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            By accessing or using FLARE Social, you confirm that you are at least 16 years
            of age and have the legal capacity to enter into a binding agreement. If you
            are using the service on behalf of an organization, you represent that you have
            the authority to bind that organization to these Terms. Your continued use of
            the service after any updates to these Terms constitutes acceptance of the
            revised Terms.
          </p>
        </div>

        {/* 2. Account Responsibilities */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            2. Account Responsibilities
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            When you create an account with FLARE Social, you agree to:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              Provide accurate, current, and complete information during registration and
              keep your account information up to date.
            </li>
            <li>
              Maintain the confidentiality of your password and account credentials. You
              are responsible for all activity that occurs under your account.
            </li>
            <li>
              Notify us immediately at{' '}
              <a
                href="mailto:legal@flare-social.com"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                legal@flare-social.com
              </a>{' '}
              if you suspect unauthorized access to your account.
            </li>
            <li>
              Not share, transfer, or sell your account credentials to any other person
              or entity.
            </li>
          </ul>
        </div>

        {/* 3. Acceptable Use */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            3. Acceptable Use
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            You agree to use FLARE Social only for lawful purposes. You must not:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              Use the service to send spam, unsolicited bulk messages, or automated
              engagement that violates the terms of any connected social media platform.
            </li>
            <li>
              Upload, publish, or distribute content that is illegal, defamatory, obscene,
              infringing, or otherwise violates any applicable law or third-party rights.
            </li>
            <li>
              Attempt to gain unauthorized access to the service, other user accounts,
              or any systems or networks connected to the service.
            </li>
            <li>
              Use the service to distribute malware, viruses, or any other harmful code.
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any part of the service, or
              circumvent any technical measures we use to protect the platform.
            </li>
            <li>
              Use the service in a manner that could overload, impair, or interfere with
              the proper functioning of the platform or any connected third-party service.
            </li>
            <li>
              Resell, sublicense, or provide the service to third parties without our
              written consent.
            </li>
          </ul>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
            We reserve the right to suspend or terminate accounts that violate these
            acceptable use policies without prior notice.
          </p>
        </div>

        {/* 4. Service Availability */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            4. Service Availability
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            We strive to maintain high availability and reliability for FLARE Social.
            However:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              The service is provided on a "best effort" basis. We do not guarantee
              100% uptime and may experience downtime for maintenance, updates, or
              unforeseen circumstances.
            </li>
            <li>
              The Free tier does not include any service level agreement (SLA). Paid plans
              are subject to the SLA terms described in their respective plan
              documentation, if applicable.
            </li>
            <li>
              We are not responsible for outages or disruptions caused by third-party
              platforms (e.g., if TikTok or Instagram APIs are temporarily unavailable).
            </li>
            <li>
              We reserve the right to modify, suspend, or discontinue any feature of the
              service at any time with reasonable notice.
            </li>
          </ul>
        </div>

        {/* 5. Billing and Refunds */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            5. Billing and Refunds
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            Paid subscriptions are processed through Stripe. By subscribing to a paid
            plan, you agree to the following:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Billing Cycle:</span>{' '}
              Subscriptions are billed on a monthly or annual basis, depending on the plan
              you select. Charges are applied at the beginning of each billing period.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Plan Changes:</span>{' '}
              When you upgrade or downgrade your plan, the change takes effect immediately.
              Upgrades are pro-rated for the remainder of the current billing cycle.
              Downgrades take effect at the start of the next billing cycle, and no partial
              refund is issued for the current period.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Cancellation:</span>{' '}
              You may cancel your subscription at any time through your account settings.
              Cancellation takes effect at the end of the current billing period. You will
              continue to have access to paid features until that period ends.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Refunds:</span>{' '}
              Refunds are handled on a case-by-case basis. If you believe you are entitled
              to a refund, contact us at{' '}
              <a
                href="mailto:legal@flare-social.com"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                legal@flare-social.com
              </a>{' '}
              within 14 days of the charge.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Failed Payments:</span>{' '}
              If a payment fails, we will attempt to charge the payment method on file
              up to three times. If all attempts fail, your account may be downgraded to
              the Free tier.
            </li>
          </ul>
        </div>

        {/* 6. Intellectual Property */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            6. Intellectual Property
          </h2>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Your Content:</span>{' '}
              You retain full ownership of all content you create, upload, or publish
              through FLARE Social. By using the service, you grant us a limited,
              non-exclusive license to store, process, and transmit your content solely
              for the purpose of providing the service (e.g., scheduling and publishing
              posts to your connected platforms).
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">AI-Generated Content:</span>{' '}
              Content generated through our AI features (such as captions and suggestions)
              is provided for your use. You own the output and may use it without
              restriction, subject to applicable law.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Our Property:</span>{' '}
              The FLARE Social platform, including its design, code, branding, logos, and
              documentation, is the intellectual property of AKA IND Technologies. You may
              not copy, modify, or distribute any part of the platform without our written
              consent.
            </li>
          </ul>
        </div>

        {/* 7. API Usage Limits */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            7. API Usage Limits
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            FLARE Social enforces API usage limits based on your subscription plan to
            ensure fair use and platform stability:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Free:</span>{' '}
              2 connected platforms, 10 scheduled posts per month, 5 AI caption credits
              per month, 10 GB media storage.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Pro:</span>{' '}
              7 connected platforms, 200 scheduled posts per month, 200 AI caption credits
              per month, 50 GB media storage.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Team:</span>{' '}
              7 connected platforms, unlimited scheduled posts, unlimited AI credits,
              500 GB media storage.
            </li>
          </ul>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-4">
            Limits reset at the beginning of each billing cycle. If you exceed your plan
            limits, additional requests may be queued or rejected until the next billing
            cycle or until you upgrade your plan. We reserve the right to modify plan
            limits with 30 days' notice.
          </p>
        </div>

        {/* 8. Termination */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            8. Termination
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            Either party may terminate this agreement at any time:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              <span className="font-medium text-gray-900 dark:text-white">By You:</span>{' '}
              You may delete your account at any time through your account settings or by
              contacting us. Upon deletion, your data will be removed in accordance with
              our Privacy Policy.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">By Us:</span>{' '}
              We may suspend or terminate your account immediately if you violate these
              Terms, engage in fraudulent activity, or if required by law. Where possible,
              we will provide notice before termination.
            </li>
            <li>
              <span className="font-medium text-gray-900 dark:text-white">Effect of Termination:</span>{' '}
              Upon termination, your right to use the service ceases immediately. We will
              retain your data for 30 days after termination to allow you to export it,
              after which it will be permanently deleted unless retention is required by
              law.
            </li>
          </ul>
        </div>

        {/* 9. Limitation of Liability */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            9. Limitation of Liability
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            To the maximum extent permitted by applicable law:
          </p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-400 leading-relaxed">
            <li>
              FLARE Social and AKA IND Technologies are provided "as is" and "as available"
              without warranties of any kind, whether express or implied, including but not
              limited to implied warranties of merchantability, fitness for a particular
              purpose, and non-infringement.
            </li>
            <li>
              In no event shall AKA IND Technologies, its directors, employees, partners,
              or affiliates be liable for any indirect, incidental, special, consequential,
              or punitive damages, including but not limited to loss of profits, data,
              business opportunities, or goodwill, arising out of or in connection with
              your use of the service.
            </li>
            <li>
              Our total aggregate liability for any claims arising out of or relating to
              these Terms or the service shall not exceed the amount you paid to us in the
              twelve (12) months preceding the event giving rise to the claim, or one
              hundred Canadian dollars (CAD $100), whichever is greater.
            </li>
            <li>
              We are not responsible for content posted by users through the service, for
              actions taken by connected social media platforms, or for any third-party
              services integrated with FLARE Social.
            </li>
          </ul>
        </div>

        {/* 10. Indemnification */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            10. Indemnification
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            You agree to indemnify, defend, and hold harmless AKA IND Technologies and its
            officers, directors, employees, and agents from and against any claims,
            liabilities, damages, losses, and expenses (including reasonable legal fees)
            arising out of or in connection with your use of the service, your violation
            of these Terms, or your violation of any rights of a third party.
          </p>
        </div>

        {/* 11. Governing Law */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            11. Governing Law
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of
            the Province of Ontario, Canada, without regard to its conflict of law
            provisions. Any disputes arising out of or relating to these Terms or the
            service shall be subject to the exclusive jurisdiction of the courts located
            in the Province of Ontario, Canada. You irrevocably consent to the jurisdiction
            and venue of such courts.
          </p>
        </div>

        {/* 12. Changes to These Terms */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            12. Changes to These Terms
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            We reserve the right to modify these Terms at any time. When we make material
            changes, we will notify you by email or by posting a prominent notice on the
            platform at least 30 days before the changes take effect. Your continued use
            of the service after the effective date of the revised Terms constitutes your
            acceptance of the changes.
          </p>
        </div>

        {/* 13. Contact */}
        <div className="card p-8 sm:p-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            13. Contact
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            If you have any questions or concerns about these Terms, please contact us:
          </p>
          <div className="mt-4 text-gray-600 dark:text-gray-400 leading-relaxed">
            <p className="font-medium text-gray-900 dark:text-white">AKA IND Technologies</p>
            <p>
              Email:{' '}
              <a
                href="mailto:legal@flare-social.com"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                legal@flare-social.com
              </a>
            </p>
            <p>
              Website:{' '}
              <a
                href="https://akaind.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-500 hover:text-sky-400 transition-colors"
              >
                akaind.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
